import { useState, useMemo, useCallback } from "react";
import { Head, router, Form, usePage, Link } from "@inertiajs/react";
import { FormSidebar } from "@/components/form_sidebar";
import { Field, FieldGroup, FieldError, FieldLabel, FieldLegend, FieldSet } from "@/components/ui/field";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Calendar } from "@/components/ui/calendar";
import dayjs from "@/lib/dayjs";
import { DAY_NAMES } from "@/lib/week_days";
import BookingsCalendar from "@/components/calendar";
import { IconDoor } from "@tabler/icons-react";
import DefaultContainer from "@/components/default_container";

export default function NewBooking({
  bookings,
  assets,
  asset_type,
  current_team,
  date: dateFromBack,
}) {
  const { permissions } = usePage().props;
  const [date, setDate] = useState(dateFromBack);

  if (assets.length === 0) {
    return (
      <DefaultContainer>
        <Head title={`Book a ${asset_type.name}`} />
        <NoAssetsState
          canManage={permissions.assets.update}
          current_team={current_team}
          asset_type={asset_type}
        />
      </DefaultContainer>
    );
  }

  const minStart = Math.min(...assets.map(a => a.working_hours_start));
  const maxEnd   = Math.max(...assets.map(a => a.working_hours_end));

  const isDateDisabled = useCallback((d) => {
    const dInTz = dayjs(d).tz(current_team.timezone);
    if (dInTz.isBefore(dayjs().tz(current_team.timezone), 'day')) return true;

    const dayName = DAY_NAMES[dInTz.day()];
    return !assets.some(a => a.working_wdays.includes(dayName));
  }, [assets, current_team.timezone]);

  const changeDate = (newDate) => {
    setDate(newDate);
    router.reload({
      data: { date: dayjs(newDate).format("YYYY-MM-DD") },
      only: ["bookings"],
      reset: ["bookings"],
      preserveState: true,
    });
  };

  const transform = (data) => {
    const { startsAtHour, startsAtMinute, endsAtHour, endsAtMinute, ...rest } = data;

    const starts_at = dayjs.tz(date, current_team.timezone)
      .set("hour", startsAtHour)
      .set("minute", startsAtMinute)
      .startOf("minute")
      .utc()
      .toISOString();

    const ends_at = dayjs.tz(date, current_team.timezone)
      .set("hour", endsAtHour)
      .set("minute", endsAtMinute)
      .startOf("minute")
      .utc()
      .toISOString();

    return { ...rest, starts_at, ends_at };
  };

  return (
    <div className="flex w-full">
      <Head title={`Book a ${asset_type.name}`} />
      <BookingsCalendar date={date} assets={assets} bookings={bookings} timezone={current_team.timezone} className="w-full" />

      <FormSidebar size="fit">
        <Form
          action={`/${current_team.slug}/asset_types/${asset_type.id}/bookings`}
          method="POST"
          transform={transform}
        >
          {({ errors }) => (
            <FieldSet>
              <FieldLegend>Book a {asset_type.name}</FieldLegend>
              <div className="flex flex-col gap-4">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={changeDate}
                  disabled={isDateDisabled}
                  className="rounded-lg border"
                />
                <AssetSelect asset_type={asset_type} assets={assets} errors={errors.asset} />
                <TimeSelect label="Starts at" hourName="startsAtHour" minuteName="startsAtMinute" minMinutes={minStart} maxMinutes={maxEnd} />
                <TimeSelect label="Ends at" hourName="endsAtHour" minuteName="endsAtMinute" minMinutes={minStart} maxMinutes={maxEnd} />

                {errors.base && (<FieldError>{errors.base}</FieldError>)}
                <Field orientation="horizontal">
                  <Button type="submit">Book</Button>
                </Field>
              </div>
            </FieldSet>
          )}
        </Form>
      </FormSidebar>
    </div>
  );
}

function NoAssetsState({ canManage, current_team, asset_type }) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
      <IconDoor className="text-muted-foreground size-12" />
      <div className="flex flex-col gap-1">
        <h2 className="font-semibold text-lg">No meeting rooms set up yet</h2>
        <p className="text-muted-foreground text-sm">
          Add at least one meeting room to start booking.
        </p>
      </div>
      {canManage && (
        <Button asChild>
          <Link href={`/${current_team.slug}/asset_types/${asset_type.id}/assets/edit`}>
            Set up meeting rooms
          </Link>
        </Button>
      )}
    </div>
  );
}

function AssetSelect({ asset_type, assets, errors }) {
  const assetOptions = useMemo(() => (
    assets.map(asset => ({ label: asset.name, value: String(asset.id) }))
  ), [assets]);

  return (
    <Field className="w-full">
      <FieldLabel>{asset_type.name}</FieldLabel>
      <Select items={assetOptions} name="asset_id">
        <SelectTrigger className="w-full bg-white">
          <SelectValue placeholder={`Select a ${asset_type.name}`} />
        </SelectTrigger>
        <SelectContent>
          <SelectGroup>
            {assetOptions.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      {errors && (<FieldError>{errors}</FieldError>)}
    </Field>
  );
}

function TimeSelect({ label, hourName, minuteName, minMinutes, maxMinutes }) {
  const hourOptions = useMemo(() => {
    const startHour = Math.floor(minMinutes / 60);
    const endHour = Math.floor(maxMinutes / 60);
    let hours = [];
    for (let i = startHour; i <= endHour; i++) {
      const currentHour = String(i).padStart(2, "0");
      hours.push({ label: currentHour, value: currentHour });
    }

    return hours;
  }, [minMinutes, maxMinutes]);

  const minuteOptions = useMemo(() => {
    let minutes = [];
    for (let i = 0; i < 60; i += 15) {
      const currentMinute = String(i).padStart(2, "0");
      minutes.push({ label: currentMinute, value: currentMinute });
    }

    return minutes;
  }, []);

  return (
    <div className="flex flex-col gap-3">
      <FieldLabel>{label}</FieldLabel>
      <div className="flex gap-1 items-center">
        <Field className="w-auto">
          <Select items={hourOptions} name={hourName}>
            <SelectTrigger className="bg-white">
              <SelectValue placeholder="00" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {hourOptions.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </Field>
        :
        <Field className="w-auto">
          <Select items={minuteOptions} name={minuteName}>
            <SelectTrigger className="bg-white">
              <SelectValue placeholder="00" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {minuteOptions.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </Field>
      </div>
    </div>
  );
}
